import * as React from 'react';
import classNames from 'classnames';
import * as Classes from '../common/classes';
import {
  FormattedInput,
  IFormattedInputBaseProps,
  ValueAndCursor,
} from '../formatted-input/FormattedInput';
import { formatDateCustom, getJavaDateTime } from '@mc/react-format';
import { Icon } from '../icon/Icon';
import { mcTime } from '@mc/react-ui-icons';
import { InputAddons } from '../input-addons/InputAddons';
import { IAddonsProps } from '../common/props';
import { parseISO } from 'date-fns';
import {
  getTimePartsCs,
  isValidTimePartsCs,
  isValidTimeValueCs,
  parseValidTimeCs,
} from './parse';
import { useEffect, useRef, useState } from 'react';
import { Popup } from '../popover/Popup';
import { PopupActions } from '../popover/reactjs-popup/types';
import { TimePicker } from './TimePicker';

// Odebrání mezer, převedení desetinné tečky na čárku apod.
const unifyEditedValue = (valueAndCursor: ValueAndCursor): ValueAndCursor => {
  const text = valueAndCursor.value;
  if (!text) {
    return { value: text };
  }

  let cursorIndex = valueAndCursor.cursorIndex || 0;
  let outputText = '';
  for (let i = 0; i < text.length; i++) {
    let c = `${text.charAt(i)}`;
    c = c
      .trim()
      // @ts-ignore
      .replaceAll(/\+/g, '')
      .replaceAll(/\s?/g, '');
    if (cursorIndex > i && c.length === 0) {
      cursorIndex--;
    }
    outputText += c;
  }

  return { value: outputText, cursorIndex: cursorIndex };
};

export interface ITimeInputProps extends IFormattedInputBaseProps<string> {
  /**
   * Pokud je true, tak hodnotou je jen čas, tedy např. 12:34
   */
  onlyTime?: boolean;
  /**
   * Den který bude pro vybraný čas použit. Pokud není vyplněn, použije se aktuální den.
   */
  referenceDate?: Date;
  readOnly?: boolean;
  fullWidth?: boolean;
  autoFocus?: boolean;
  onBlur?: (value?: string, isValid?: boolean) => void;
}

export type TimeInputProps = ITimeInputProps & IAddonsProps;

const isValidInput = (value: string) => {
  let parts = getTimePartsCs(value);
  if (parts === undefined) {
    return false;
  }

  return isValidTimePartsCs(parts);
};

const isValidValue = (value: string) => {
  let parts = getTimePartsCs(value);
  if (parts === undefined) {
    return false;
  }

  return isValidTimeValueCs(parts);
};

export const formatTimeViewISOValue = (value: string) => {
  return formatDateCustom(value, { timeFormat: 'minute' });
};
export const formatTimeViewDateValue = (value: Date) => {
  return formatDateCustom(value, { timeFormat: 'minute' });
};
const formatViewTimeValue = (value: string) => {
  return value;
};

export const formatTimeInputValue = (value: string, onlyTime?: boolean) => {
  let res;
  if (onlyTime) {
    res = formatViewTimeValue(value);
  } else {
    res = formatTimeViewISOValue(value);
  }
  return res || '';
};

export const parseTimeStrValueToISO = (value: string, referenceDate?: Date) => {
  let parts = getTimePartsCs(value);
  if (!parts) {
    return undefined;
  }

  const dt = parseValidTimeCs(parts, referenceDate ?? new Date());
  return dt ? getJavaDateTime(dt) || undefined : undefined;
};

export const parseValueToTime = (value: string) => {
  let parts = getTimePartsCs(value);
  if (!parts) {
    return undefined;
  }
  let hour = parseInt(parts[0]);
  let min = parts[1] !== '' ? parseInt(parts[1]) : 0;
  return ('0' + hour).slice(-2) + ':' + ('0' + min).slice(-2);
};

/**
 * Komponenta pro editaci času.
 */
export const TimeInput: React.FC<TimeInputProps> = ({
  className,
  onlyTime,
  addonAfter,
  addonAfterCompact,
  disabled,
  readOnly,
  fullWidth,
  dataTest,
  autoFocus,
  onBlur,
  onChange,
  referenceDate,
  value,
  ...other
}) => {
  const [isValid, setIsValid] = useState<boolean>(true);
  const [opened, setOpened] = useState<boolean>(false);

  //následující dva state jsou použité POUZE pro:
  //nastavení celkového času při částečném zadání (vyberu jednu hodnotu a kliknu mimo popup)
  const [hoursPartial, setHoursPartial] = useState<number>();
  const [minutesPartial, setMinutesPartial] = useState<number>();

  useEffect(() => {
    // pokud je popup zavřený a je nastavena alepoň jedna částečná hodnota
    if (!opened && (hoursPartial || minutesPartial)) {
      let hoursNew, minutesNew;
      if (!value) {
        // použití částečných hodnot, pokud jsou dostupné, jinak použije aktuální hodiny/0 minut
        const actualHour = new Date().getHours();
        hoursNew = hoursPartial ?? actualHour;
        minutesNew = minutesPartial ?? 0;
      } else {
        // pokud je hodnota, tak ji formátuje a rozdělí na hodiny a minuty
        const formatTime = formatTimeInputValue(value, onlyTime);
        const [hoursInit, minutesInit] = formatTime
          .split(':')
          .map((time) => parseInt(time, 10));

        // použití částečných hodnot, pokud jsou dostupné, jinak použije původní hodnoty
        hoursNew = hoursPartial ?? hoursInit;
        minutesNew = minutesPartial ?? minutesInit;
      }

      // kombinace hodin a minut do časového řetězce
      let result;
      const time = `${hoursNew}:${minutesNew}`;

      // převedení hodnoty na formát dle onlyTime
      if (onlyTime) {
        result = parseValueToTime(time);
      } else {
        result = parseTimeStrValueToISO(time, referenceDate);
      }

      // resetování částečných hodnot
      setHoursPartial(undefined);
      setMinutesPartial(undefined);

      // aplikace změny do formuláře
      onChange?.(result);
      onBlur?.(result);
    }
  }, [opened, hoursPartial, minutesPartial]);

  const popupRef = useRef<PopupActions | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement | undefined>(undefined);

  const doPopupOpen = () => {
    popupRef.current?.open();
    setOpened(true);
  };

  const handlePopupClose = () => {
    const input = inputRef.current;
    if (input) {
      const length = input.value.length;
      input.setSelectionRange(length, length);
      input.focus();
    }
    setOpened(false);
  };

  const input = (
    <div
      key={'input'}
      className={classNames(
        className,
        Classes.TIME_INPUT,
        Classes.FIELD_INPUT,
        {
          [Classes.FIELD_INPUT_DISABLED]: disabled,
          [Classes.FIELD_INPUT_READONLY]: readOnly,
          [Classes.LIMIT_DATE_TIME_SIZE]: !fullWidth,
        }
      )}
      {...other}
    >
      <Popup
        nested={true}
        open={opened}
        // @ts-ignore
        innerRef={popupRef}
        onClose={handlePopupClose}
        on={[]}
        trigger={
          <div key="input-wrapper" className={Classes.FIELD_INPUT_GROUP}>
            <FormattedInput<string>
              dataTest={dataTest}
              compareValues={(value1, value2) => {
                if (value1 && value2) {
                  if (onlyTime) {
                    return value1 === value2;
                  } else {
                    const v1 = parseISO(value1);
                    const v2 = parseISO(value2);
                    return (
                      v1.getHours() === v2.getHours() &&
                      v1.getMinutes() === v2.getMinutes() &&
                      v1.getSeconds() === v2.getSeconds()
                    );
                  }
                } else {
                  return value1 === value2;
                }
              }}
              className={classNames({})}
              inputClassName={classNames(
                Classes.INPUT_GROUP_INPUT,
                Classes.INPUT,
                {
                  [Classes.ACTIVE]: opened,
                }
              )}
              onBlur={(value) => {
                onBlur?.(value, isValid);
              }}
              value={value}
              onChange={onChange}
              inputRef={inputRef}
              {...other}
              isValidInput={isValidInput}
              isValidValue={(value) => {
                const isNewValid = isValidValue(value);
                setIsValid(isNewValid);
                return isNewValid;
              }}
              formatViewValue={(value) => formatTimeInputValue(value, onlyTime)}
              disabled={disabled}
              readOnly={readOnly}
              unifyEditedValue={unifyEditedValue}
              rightAction={
                <div
                  key={'icon'}
                  tabIndex={-1}
                  className={Classes.FIELD_INPUT_ACTION}
                  onClick={disabled || readOnly ? undefined : doPopupOpen}
                >
                  <Icon fixedWidth icon={mcTime} />
                </div>
              }
              parseValue={
                onlyTime
                  ? parseValueToTime
                  : (value) => parseTimeStrValueToISO(value, referenceDate)
              }
              autoFocus={autoFocus}
            />
          </div>
        }
      >
        <TimePicker
          onlyTime={onlyTime}
          value={value}
          onChange={onChange}
          setHoursPartial={setHoursPartial}
          setMinutesPartial={setMinutesPartial}
          onBlur={(value) => {
            onBlur?.(value, true);
          }}
          onClose={handlePopupClose}
        />
      </Popup>
    </div>
  );

  return (
    <InputAddons
      limitedSize
      addonAfter={addonAfter}
      addonAfterCompact={addonAfterCompact}
    >
      {input}
    </InputAddons>
  );
};
